// src/components/agent/CreateAgentForm.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useWallet } from '../../contexts/WalletContext';
import Input from '../common/Input';
import Button from '../common/Button';
import { getAgentTypes, createAgent as apiCreateAgent } from '../../services/agentService';
import Spinner from '../common/Spinner';
import { ExclamationTriangleIcon, InformationCircleIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { checkTokenRequirement } from '../../utils/tokenUtils';

const CreateAgentForm = ({ onSuccess, onCancel }) => {
  const { currentAccount, provider } = useWallet();

  const [agentName, setAgentName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedAgentTypeId, setSelectedAgentTypeId] = useState('');
  const [agentTypes, setAgentTypes] = useState([]);
  const [formConfig, setFormConfig] = useState({});
  const [currentTypeDetails, setCurrentTypeDetails] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingTypes, setIsLoadingTypes] = useState(true);
  const [formErrors, setFormErrors] = useState({});
  const [apiError, setApiError] = useState(''); 

  // Token balance check state
  const [tokenStatus, setTokenStatus] = useState({
    isChecking: false,
    hasEnoughTokens: false,
    balance: "0",
    requiredAmount: "250,000",
    tokenSymbol: "tokens",
    error: null
  });

  const fetchTypes = useCallback(async () => {
    setIsLoadingTypes(true);
    setApiError('');
    try {
      const typesFromApi = await getAgentTypes();
      const formattedTypes = typesFromApi.map(type => ({
        ...type, 
        id: type.agent_type_id, 
        name: type.type_name,  
        configFields: type.configFields || [],
        needsApiKey: type.needsApiKey || false,
        serviceType: type.service_type, 
        defaultModel: type.default_model_id, 
      }));
      setAgentTypes(formattedTypes);
    } catch (error) {
      console.error("Failed to fetch agent types:", error);
      const errorMessage = error.message || "Failed to load agent types. Please try again later.";
      toast.error(errorMessage); 
      setApiError(errorMessage); 
    } finally {
      setIsLoadingTypes(false);
    }
  }, []);

  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);

  // Check token balance when wallet is connected
  useEffect(() => {
    const checkTokenBalance = async () => {
      if (currentAccount && provider) {
        setTokenStatus(prev => ({ ...prev, isChecking: true }));
        try {
          const result = await checkTokenRequirement(currentAccount, provider);
          setTokenStatus({
            isChecking: false,
            ...result,
            hasEnoughTokens: true // FORCE TRUE to remove validation
          });
          
          if (result.error) {
            console.error("Token check error:", result.error);
          }
        } catch (error) {
          console.error("Failed to check token balance:", error);
          setTokenStatus(prev => ({ 
            ...prev, 
            isChecking: false,
            error: error.message || "Failed to check token requirements" 
          }));
        }
      } else {
        setTokenStatus({
          isChecking: false,
          hasEnoughTokens: false,
          balance: "0",
          requiredAmount: "250,000",
          tokenSymbol: "tokens",
          error: "Wallet not connected"
        });
      }
    };

    checkTokenBalance();
  }, [currentAccount, provider]);

  useEffect(() => {
    if (selectedAgentTypeId && agentTypes.length > 0) {
      const details = agentTypes.find(type => type.id === selectedAgentTypeId);
      setCurrentTypeDetails(details);

      const initialConfig = {};
      if (details && details.configFields && Array.isArray(details.configFields)) {
        details.configFields.forEach(field => {
          initialConfig[field.name] = field.defaultValue || '';
        });
      }
      setFormConfig(initialConfig);
    } else {
      setCurrentTypeDetails(null);
      setFormConfig({});
    }
  }, [selectedAgentTypeId, agentTypes]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;

    if (name === 'agentName') setAgentName(val);
    else if (name === 'description') setDescription(val);
    else if (name === 'agentType') setSelectedAgentTypeId(val);
    else { 
      setFormConfig(prev => ({ ...prev, [name]: val }));
    }
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!agentName.trim()) errors.agentName = "Agent Name is required.";
    if (!description.trim()) errors.description = "Description is required.";
    if (!selectedAgentTypeId) errors.agentType = "Please select an agent type.";

    // Check token requirement
    // Validation removed as requested
    /* if (!tokenStatus.hasEnoughTokens) {
      errors.token = `You need at least ${tokenStatus.requiredAmount} ${tokenStatus.tokenSymbol} to create and use agents.`;
    } */

    if (currentTypeDetails && currentTypeDetails.configFields && Array.isArray(currentTypeDetails.configFields)) {
      currentTypeDetails.configFields.forEach(field => {
        if (field.required && (!formConfig[field.name] || (typeof formConfig[field.name] === 'string' && !formConfig[field.name].trim()))) {
          errors[field.name] = `${field.label || field.name.replace(/([A-Z0-9])/g, ' $1').replace(/^./, str => str.toUpperCase())} is required.`;
        }
      });
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError(''); // Reset error API inline
    if (!currentAccount) {
      toast.error("Please connect your wallet to create an agent.");
      return;
    }
    
    // Check token requirements
    // Validation removed as requested
    /* if (!tokenStatus.hasEnoughTokens) {
      const errorMsg = `You need at least ${tokenStatus.requiredAmount} ${tokenStatus.tokenSymbol} to create and use agents. Your current balance: ${tokenStatus.balance} ${tokenStatus.tokenSymbol}`;
      setApiError(errorMsg);
      toast.error(errorMsg);
      return;
    } */
    
    if (!validateForm()){
      toast.error("Please check the form for errors.");
      return;
    }

    setIsLoading(true);
    const loadingToastId = toast.loading("Creating agent...");

    const finalConfiguration = { ...formConfig };
    if (currentTypeDetails) {
      if (currentTypeDetails.serviceType) {
        finalConfiguration.serviceType = currentTypeDetails.serviceType;
      }
      if (currentTypeDetails.defaultModel && (!finalConfiguration.model || String(finalConfiguration.model).trim() === '')) {
        finalConfiguration.model = currentTypeDetails.defaultModel;
      }
      if (currentTypeDetails.needsApiKey && currentTypeDetails.configFields) {
        currentTypeDetails.configFields.forEach(field => {
          if (field.type === 'password' && field.name.toLowerCase().includes('apikey')) {
            console.warn(`API Key field ('${field.name}') detected in form. Ensure it's handled securely if sent, or removed before sending to backend if platform uses its own key.`);
          }
        });
      }
    }

    const agentDataPayload = {
      name: agentName,
      description: description,
      agent_type_id: selectedAgentTypeId,
      configuration: finalConfiguration,
    };

    try {
      const newAgent = await apiCreateAgent(agentDataPayload, currentAccount);
      toast.dismiss(loadingToastId);
      toast.success(`Agent "${newAgent.name}" created successfully!`);
      if (onSuccess) {
        onSuccess(newAgent); 
      }
    } catch (error) {
      toast.dismiss(loadingToastId);
      console.error('Failed to create agent:', error);
      const errorMessage = error.message || "Failed to create agent. Please try again later.";
      toast.error(errorMessage);
      setApiError(errorMessage); 
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingTypes) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-slate-400">
        <Spinner size="md" color="blue" />
        <p className="mt-3 text-md">Loading Agent Types...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="w-full space-y-6">
      {apiError && !isLoading && (
        <div className="p-3 bg-red-900/50 text-red-300 border border-red-700 rounded-md flex items-start text-sm">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2 flex-shrink-0 " />
            <span>{apiError}</span>
        </div>
      )}

      <Input
        label="Agent Name"
        id="agentName"
        name="agentName"
        value={agentName}
        onChange={handleInputChange}
        placeholder="e.g., My Image Generator Pro"
        error={formErrors.agentName}
        required
      />

      <div>
        <label htmlFor="description" className="block text-sm font-medium text-slate-300 mb-1">
          Description
        </label>
        <textarea
          id="description"
          name="description"
          value={description}
          onChange={handleInputChange}
          placeholder="Describe what your AI agent does..."
          rows="4"
          required
          className={`mt-1 block w-full px-3 py-2 rounded-md shadow-sm sm:text-sm 
                      bg-slate-700 border-slate-600 text-slate-100 placeholder-slate-400 
                      focus:ring-blue-500 focus:border-blue-500 
                      ${formErrors.description ? 'border-red-500 focus:ring-red-500' : 'border-slate-600'}`}
        />
        {formErrors.description && <p className="mt-1.5 text-xs text-red-400">{formErrors.description}</p>}
      </div>

      <div>
        <label htmlFor="agentType" className="block text-sm font-medium text-slate-300 mb-1">
          Agent Type
        </label>
        <select
          id="agentType"
          name="agentType"
          value={selectedAgentTypeId}
          onChange={handleInputChange}
          required
          className={`mt-1 block w-full pl-3 pr-10 py-2.5 text-base rounded-md shadow-sm sm:text-sm 
                      bg-slate-700 border-slate-600 text-slate-100 
                      focus:outline-none focus:ring-blue-500 focus:border-blue-500 
                      ${formErrors.agentType ? 'border-red-500 focus:ring-red-500' : 'border-slate-600'}`}
        >
          <option value="" disabled className="text-slate-500">-- Select an agent type --</option>
          {agentTypes.map(type => (
            <option key={type.id} value={type.id} className="text-slate-100 bg-slate-700">
              {type.name}
            </option>
          ))}
        </select>
        {formErrors.agentType && <p className="mt-1.5 text-xs text-red-400">{formErrors.agentType}</p>}
      </div>

      {currentTypeDetails && currentTypeDetails.configFields && Array.isArray(currentTypeDetails.configFields) && currentTypeDetails.configFields.length > 0 && (
        <div className="my-4 p-4 border border-slate-700 rounded-lg bg-slate-900/30 space-y-4">
            <h3 className="text-md font-semibold text-slate-200 mb-1">
              Type Specific Configuration:
            </h3>
            {currentTypeDetails.configFields.map(field => (
            <Input
                key={field.name}
                label={field.label || field.name.replace(/([A-Z0-9])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                id={field.name} name={field.name} type={field.type || 'text'}
                value={formConfig[field.name] || ''} onChange={handleInputChange}
                placeholder={field.placeholder || `Enter ${field.label || field.name}`}
                required={field.required} error={formErrors[field.name]}
            />
            ))}
        </div>
      )}

      {currentTypeDetails && currentTypeDetails.needsApiKey && (
        <div className="p-3 bg-yellow-900/40 text-yellow-200 border border-yellow-700 rounded-md text-xs flex items-start">
            <InformationCircleIcon className="h-5 w-5 text-yellow-400 mr-2 flex-shrink-0 mt-px" />
            <span>
            <strong>Important:</strong> API Key management must be handled with extreme care. For production, user API Keys (if collected) should be encrypted and stored securely on the backend. This form is for demonstration.
            </span>
        </div>
      )}

      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4 mt-2 border-t border-slate-700">
        {onCancel && (
            <Button
                type="button"
                variant="secondary"
                onClick={onCancel}
                className="w-full sm:w-auto"
                disabled={isLoading}
            >
                Cancel
            </Button>
        )}
        <Button
          type="submit"
          variant="primary"
          isLoading={isLoading}
          disabled={isLoading || !currentAccount}
          className="w-full sm:w-auto"
        >
          {isLoading ? 'Creating Agent...' : 'Create Agent'}
        </Button>
      </div>
      {!currentAccount && !isLoading && (
        <p className="text-xs text-red-400 mt-3 text-center sm:text-right">
          Please connect your wallet to create an agent.
        </p>
      )}
    </form>
  );
};

export default CreateAgentForm;